import React, { Fragment, PropsWithChildren, useMemo } from 'react';
import { Box, List, Typography } from '@mui/material';
import { HeaderPrimaryButton, PageHeader } from 'components/layout/PageHeader';
import { ListSkeleton } from 'components/skeletons';
import { EmptyState } from 'components/empty.state';
import { WorkOutlineOutlined, TouchAppOutlined } from '@mui/icons-material';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from "date-fns";
import { fr } from 'date-fns/locale';
import { useGetOffers } from 'core/offers/hooks';
import { ListCard } from 'components/list/list.card';

const menuSidebarWidth: string = "24rem";

type OfferProps = {
    offer: any;
    selected: boolean;
};

const OfferCard = ({offer, selected}: OfferProps) => {
    const chips = useMemo(() => {
        const chips: any[] = [
            {label: offer.source, color: 'info'},
            {label: offer.job_contract, color: 'success'},
        ];

        if (offer.application_count === 1) chips.push({label: `${offer.application_count} candidat`, color: 'warning'});
        if (offer.application_count > 1) chips.push({label: `${offer.application_count} candidats`, color: 'warning'});

        return chips;
    }, [offer]);

    return (
        <ListCard
            title={offer.job_title}
            selected={selected}
            subtitle={`Posté le ${format(Date.parse(offer.posting_date), 'dd/MM/yy', {locale: fr})}`}
            component={Link}
            to={`/dashboard/offers/${offer.id}`}
            chips={chips}
        />
    );
};

const IndexPage = ({children}: PropsWithChildren) => {
    const {offerId} = useParams();
    const {data, isLoading, isError} = useGetOffers({onError: (err) => toast.error("Une Erreur est survenue")});

    if (!isLoading && (isError || data === undefined)) { return (<Typography>Une erreur est survenue</Typography>); }

    return (
        <Fragment>
            <PageHeader
                title='Missions'
                actions={<HeaderPrimaryButton component={Link} to='/dashboard/offers/new'>Ajouter une mission</HeaderPrimaryButton>}
            />
            <Box sx={{display: 'flex', gap: 0, justifyContent: 'space-between'}}>
                <List sx={{width: menuSidebarWidth, flexShrink: 0, p: '1rem', height: 'calc(100vh - 72px)', overflowY: 'scroll'}} disablePadding>
                    {isLoading && <ListSkeleton />}
                    {!isLoading && (data?.offers || []).length === 0 && (
                        <EmptyState
                            icon={<WorkOutlineOutlined />}
                            title='Aucune mission'
                            description="Publiez votre première mission pour commencer à recevoir des candidatures."
                            action={<HeaderPrimaryButton component={Link} to='/dashboard/offers/new' sx={{ boxShadow: 'none' }}>Ajouter une mission</HeaderPrimaryButton>}
                        />
                    )}
                    {(data?.offers || []).map((offer: any) =>
                        <OfferCard key={offer.id} offer={offer} selected={offerId === offer.id.toString()} />
                    )}
                </List>
                <Box sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, pr: '1rem', py: '1rem', height: 'calc(100vh - 72px)', overflowY: 'scroll' }}>
                    {children ? (
                        <Box sx={{ borderRadius: '1rem', border: '1px solid #EDF0F3', boxShadow: 1, backgroundColor: 'background.paper', minWidth: 0, overflow: 'hidden' }}>
                            {children}
                        </Box>
                    ) : (
                        <EmptyState
                            size='md'
                            variant='dashed'
                            icon={<TouchAppOutlined />}
                            title="Sélectionnez une mission"
                            description="Cliquez sur une mission pour voir son détail et ses candidats."
                            sx={{ minHeight: 'calc(100vh - 72px - 2rem)' }}
                        />
                    )}
                </Box>
            </Box>
        </Fragment>
    );
};

export { IndexPage };